import { type GraphQLEndpointMap } from '../routing/apiRouting';

export type RuntimeEnvironment = Record<string, string | undefined>;

type RequiredGraphQLHostnameEnvKey = 'FIELD_OPS_HOSTNAME' | 'CORE_HOSTNAME';

const REQUIRED_GRAPHQL_HOSTNAME_ENV_KEYS: RequiredGraphQLHostnameEnvKey[] = ['FIELD_OPS_HOSTNAME', 'CORE_HOSTNAME'];

const readRequiredEnvVar = (env: RuntimeEnvironment, key: RequiredGraphQLHostnameEnvKey): string => {
  const value = env[key]?.trim();

  if (!value) {
    throw new Error(
      `Missing required environment variable "${key}" for client GraphQL routing configuration. Set both FIELD_OPS_HOSTNAME and CORE_HOSTNAME.`,
    );
  }

  return value;
};

const deriveGraphQLEndpoint = (hostname: string, path: '/graphql' | '/client/graphql/'): string => {
  const normalizedHostname = hostname.replace(/\/+$/, '');

  return `${normalizedHostname}${path}`;
};

export const resolveRuntimeEnvWithFallbacks = ({
  runtimeEnv,
  fallbackEnv,
}: {
  runtimeEnv?: RuntimeEnvironment;
  fallbackEnv?: RuntimeEnvironment;
}): RuntimeEnvironment => {
  const resolvedEnv: RuntimeEnvironment = {};

  for (const key of REQUIRED_GRAPHQL_HOSTNAME_ENV_KEYS) {
    resolvedEnv[key] = runtimeEnv?.[key] ?? fallbackEnv?.[key];
  }

  return resolvedEnv;
};

export const resolveGraphQLEndpoints = (env: RuntimeEnvironment): GraphQLEndpointMap => {
  const fieldOpsHostname = readRequiredEnvVar(env, 'FIELD_OPS_HOSTNAME');
  const coreHostname = readRequiredEnvVar(env, 'CORE_HOSTNAME');

  return {
    'field-ops': deriveGraphQLEndpoint(fieldOpsHostname, '/graphql'),
    core: deriveGraphQLEndpoint(coreHostname, '/client/graphql/'),
  };
};
